import type { FeatureRelease } from '~/types/feature-release'

const FEATURE_RELEASES: FeatureRelease[] = [
    {
        id: 'v0.3.0',
        releaseDate: '2026-03-23',
        theme: 'New games and live session tools',
        featureDrop: 'Sound Alike, Time Attack, Battle of the Views, Alpha Mix, scheduled events, and recap sharing',
        announcementAngle: 'Vibe Games now has more ways to play, more ways to host, and more ways to bring people back.',
        status: 'SHIPPED',
        summary: 'V0.3.0 expands the game lineup, adds public scheduled events, and improves recap sharing and hostless session flow.',
        highlights: [
            'Added Sound Alike, Time Attack, and Battle of the Views',
            'Added an unlockable "Alpha Mix" game upon completing the core games',
            'Added scheduled game events with public pages, countdowns, and calendar export',
            'Added recap referral pages and smoother session handoffs between games',
        ],
    },
    {
        id: 'v0.2.0',
        releaseDate: '2026-02-14',
        theme: 'Hole in the Wall polish',
        featureDrop: 'New block shapes, clearer silhouettes, and a quicker path back to host controls',
        announcementAngle: 'Hole in the Wall is cleaner to read and easier to run.',
        status: 'SHIPPED',
        summary: 'The latest shipped update focused on core readability and smoother host flow.',
        highlights: [
            'New block shapes in Hole in the Wall',
            'Improved character silhouettes',
            'Quick link to host controls from player view',
        ],
    },
    {
        id: 'R-01',
        releaseDate: '2026-03-19',
        theme: 'Tight Rope polish',
        featureDrop: 'Game & Watch animation pass + danger-zone fall threshold + manual tilt stability',
        announcementAngle: 'Tight Rope now has authentic choppy fall animation and stricter balance stakes.',
        status: 'PLANNED',
        summary: 'Sharpen Tight Rope movement, danger states, and overall physical feel.',
        highlights: [],
    },
    {
        id: 'R-02',
        releaseDate: '2026-04-02',
        theme: 'Tight Rope reveal',
        featureDrop: 'Winner/loser spotlight on reveal board + mangled winner state when both fall',
        announcementAngle: 'Reveal moments now tell the full story of who survived and who wiped out.',
        status: 'PLANNED',
        summary: 'Make Tight Rope reveal boards more dramatic and easier to read at a glance.',
        highlights: [],
    },
    {
        id: 'R-03',
        releaseDate: '2026-04-16',
        theme: 'Sound Alike depth',
        featureDrop: 'Better reveal pacing + clearer audio judging flow',
        announcementAngle: 'Sound Alike rounds are faster to judge and more fun to watch live.',
        status: 'PLANNED',
        summary: 'Improve pacing and judging clarity for Sound Alike rounds.',
        highlights: [],
    },
    {
        id: 'R-04',
        releaseDate: '2026-04-30',
        theme: 'Hole in the Wall content',
        featureDrop: 'New shape pack + capture UX polish',
        announcementAngle: 'Fresh wall shapes just dropped for more chaotic rounds.',
        status: 'PLANNED',
        summary: 'Expand Hole in the Wall content while tightening the player capture flow.',
        highlights: [],
    },
    {
        id: 'R-05',
        releaseDate: '2026-05-14',
        theme: 'Time Attack variety',
        featureDrop: 'Additional Time Attack modes and scoring flair',
        announcementAngle: 'More ways to clutch Time Attack with new challenge variants.',
        status: 'PLANNED',
        summary: 'Add more Time Attack variety so sessions stay fresh and more replayable.',
        highlights: [],
    },
    {
        id: 'R-06',
        releaseDate: '2026-05-28',
        theme: 'Ghost Players alpha',
        featureDrop: 'Replay-driven ghost players for low-lobby sessions',
        announcementAngle: 'Dead lobbies are out. Ghost players keep every session active.',
        status: 'PLANNED',
        summary: 'Keep smaller sessions moving by filling them with replay-driven ghost players.',
        highlights: [],
    },
    {
        id: 'R-07',
        releaseDate: '2026-06-11',
        theme: 'Host experience',
        featureDrop: 'Host-side visibility and control upgrades',
        announcementAngle: 'Hosts get clearer game control with less manual overhead.',
        status: 'PLANNED',
        summary: 'Improve host confidence with better visibility and simpler controls.',
        highlights: [],
    },
    {
        id: 'R-08',
        releaseDate: '2026-06-25',
        theme: 'Shareability',
        featureDrop: 'Shareable recap package V1',
        announcementAngle: 'Every game night now ships with a share-ready recap moment.',
        status: 'PLANNED',
        summary: 'Turn finished sessions into something players can actually share and bring friends back through.',
        highlights: [],
    },
]

const compareByDateDesc = (left: FeatureRelease, right: FeatureRelease) => {
    return new Date(right.releaseDate).getTime() - new Date(left.releaseDate).getTime()
}

const compareByDateAsc = (left: FeatureRelease, right: FeatureRelease) => {
    return new Date(left.releaseDate).getTime() - new Date(right.releaseDate).getTime()
}

export const getFeatureReleases = (): FeatureRelease[] => {
    return FEATURE_RELEASES.map((release) => ({
        ...release,
        highlights: [...release.highlights],
    }))
}

export const getLatestShippedFeatureRelease = (): FeatureRelease | null => {
    return getFeatureReleases()
        .filter((release) => release.status === 'SHIPPED')
        .sort(compareByDateDesc)[0] ?? null
}

export const getPlannedFeatureReleases = (): FeatureRelease[] => {
    return getFeatureReleases()
        .filter((release) => release.status === 'PLANNED')
        .sort(compareByDateAsc)
}

export const getShippedFeatureReleases = (): FeatureRelease[] => {
    return getFeatureReleases()
        .filter((release) => release.status === 'SHIPPED')
        .sort(compareByDateDesc)
}
